import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Button,
  Container,
  IconButton,
  Paper,
  Slide,
  Fade
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';

import { startSearch, setFilter } from "../../actions/jobActions";
import MapSearch from "./JobSearch/MapSearch";
import SearchResult from "./JobSearch/SearchResult";
import JobFilter from "./JobFilter";
import AreaSelect from "../common/AreaSelect";
import FilterSummary from "./JobSearch/FilterSummary";
import PageLoading from "../common/PageLoading";
import { getNewNotificationNum } from "../../utils/getNotificationNum";

const DEFAULT_PAGE_SIZE = 20;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fffbf9',
    position: 'relative',
    width: '100%',
    paddingTop: '9rem'
  },
  container: {
    width: '100%',
    display: 'flex',
    position: 'relative'
  },
  filter: {
    position: 'fixed',
    width: '100%',
    top: '22rem',
    maxHeight: 'calc(100vh - 22rem)',
    overflowY: 'auto',
    backgroundColor: '#ebf7fe',
    border: `1px solid ${theme.palette.primary.dark}`,
    zIndex: 3,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shorter
    }),
    '& > *': {
      backgroundColor: '#ebf7fe',
    }
  },
  fixedArea: {
    position: 'fixed',
    backgroundColor: '#ebf7fe',
    margin: 0,
    top: '13rem', // Header (8rem) + GlobalNav (5rem)
    left: 0,
    padding: theme.spacing(2),
    width: '100%',
    height: '9rem',
    boxSizing: 'border-box',
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderRadius: '0'
  },
  fixedAreaContainer: {
    margin: theme.spacing(0, 14),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, 2)
    }
  },
  header: {
    display: 'inline-block',
    fontSize: '2.4rem',
    color: theme.palette.primary.dark,
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  areaContainer: {
    marginLeft: theme.spacing(4),
    display: 'flex',
    alignItems: 'center'
  },
  areaLabel: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  filterContainer: {
    marginLeft: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
  },
  filterLabel: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  filterButton: {
    minWidth: 'unset',
    padding: theme.spacing(1),
    backgroundColor: '#52bbf8',
    borderRadius: '20%',
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '2.4rem',
    }
  },
  warningBox: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    borderRadius: theme.shape.borderRadius
  },
  searchResult: {
    padding: theme.spacing(2),
    width: '100%',
    minHeight: '50vh'
  }
}));

function JobSearch(props) {
  const classes = useStyles();
  const filter = useSelector(state => state.job.searchFilter);
  const currentUser = useSelector(state => state.user.currentUser);
  const currentPage = useSelector(state => state.job.currentPage);
  const notifications = useSelector(state => state.user.notifications);

  let notificationNum = getNewNotificationNum(notifications);

  const dispatch = useDispatch();

  const [useMap, setUseMap] = useState(false);
  const [filterOpened, setFilterOpen] = useState(false);

  useEffect(() => {
    dispatch(startSearch(filter, currentPage, DEFAULT_PAGE_SIZE));
    window.scrollTo(0, 0);
  }, [dispatch, filter, currentPage]);

  const handleAreaChange = areaId => {
    let newFilter = { ...filter };
    newFilter.area = areaId ? areaId : undefined;
    dispatch(setFilter(newFilter));
  };

  const handleSaveFilter = savedFilter => {
    setFilterOpen(false);
    let newFilter = { ...filter };
    newFilter = Object.assign(newFilter, savedFilter);
    dispatch(setFilter(newFilter));
  };

  const handleMapChange = data => {
    console.log("Not implemented yet");
  };

  return (
    <Box className={classes.root}>
      <Helmet>
        <title>{notificationNum ? `(${notificationNum})` : ""}求人検索</title>
        <meta
          name="description"
          content="ニュージーランドの求人情報をエリア、カテゴリ等様々な検索条件を指定して探す事ができます。"
        />
      </Helmet>

      <Paper className={classes.fixedArea} elevation={1}>
        <Box className={classes.fixedAreaContainer}>
          <Typography variant="h1" className={classes.header}>
            求人検索
          </Typography>

          <Box className={classes.areaContainer}>
            <Typography variant="h4" className={classes.areaLabel}>
              エリア
            </Typography>
            <AreaSelect
              onChange={handleAreaChange}
              emptyString="エリア指定無し"
              value={filter.area}
            />
          </Box>

          <Box className={classes.filterContainer}>
            <Typography variant="h4" className={classes.filterLabel}>
              フィルタ
            </Typography>
            <IconButton
              color="primary"
              onClick={() => setFilterOpen(!filterOpened)}
              className={classes.filterButton}
              size="medium"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Slide direction="down" in={filterOpened} mountOnEnter unmountOnExit>
        <Box className={classes.filter}>
          <Box style={{ paddingBottom: '2rem' }}>
            <JobFilter onSaveFilter={filter => handleSaveFilter(filter)} />
          </Box>
        </Box>
      </Slide>

      <FilterSummary filter={filter} />

      {currentUser?.profile.user_type === "Business" && (
        <Box className={classes.warningBox}>
          <Typography>
            新しい求人広告を投稿するには、右上の『メニュー』をクリックして『求人投稿・編集』を選んでください。
          </Typography>
        </Box>
      )}

      <Box className={classes.container}>
        <Fade in={true} timeout={300}>
          <Box className={classes.searchResult}>
            {useMap ? (
              <MapSearch onChange={handleMapChange} />
            ) : (
              <SearchResult pageSize={DEFAULT_PAGE_SIZE} />
            )}
            <PageLoading animationClass="fade" />
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}

export default JobSearch;
